// covered-by: frontends/carrie/src/config/__tests__/askSurface.test.ts
/**
 * Host gate for AskPuntt V2.
 *
 * V2 mounts when the page is served on an Ask host (ask.puntt.ai,
 * ask-dev.puntt.ai, ask-staging.puntt.ai, ask.localhost), when a local
 * override is set (`?surface=ask` in DEV, or `VITE_ASK_SURFACE=true`),
 * or when the in-place marker is set after the `ask-puntt-v2` PostHog
 * flag (same origin as app.puntt.ai so login does not run twice).
 *
 * Keep this module free of route imports — router.ts and the landing helper
 * both call it, and a route import here would cycle the tree.
 */

import { ASK_SURFACE_ENV } from "./env";

const ASK_PUNTT_HOST = /^ask\.puntt\.ai$/i;
const ASK_STAGE_HOST = /^ask-[a-z0-9-]+\.puntt\.ai$/i;
const ASK_LOCAL_HOST = /^ask\.localhost$/i;

/** Set after PostHog `ask-puntt-v2` so the next boot mounts the V2 tree. */
export const ASK_V2_INPLACE_STORAGE = "ask-v2-inplace";

export function isAskHostname(hostname: string): boolean {
  const host = hostname.split(":")[0]?.toLowerCase() ?? "";
  return (
    ASK_PUNTT_HOST.test(host) ||
    ASK_STAGE_HOST.test(host) ||
    ASK_LOCAL_HOST.test(host)
  );
}

export type AskSurfaceInput = {
  hostname?: string;
  search?: string;
  envFlag?: string | boolean | undefined;
  inPlace?: boolean;
  /** Override DEV-gating of `?surface=ask`. Tests pass this explicitly. */
  allowQueryOverride?: boolean;
};

function readWindowHostname(): string {
  if (typeof window === "undefined") return "";
  return window.location.hostname;
}

function readWindowSearch(): string {
  if (typeof window === "undefined") return "";
  return window.location.search;
}

function envFlagOn(envFlag: string | boolean | undefined): boolean {
  return envFlag === true || envFlag === "true" || envFlag === "1";
}

function queryOverrideAllowed(input: AskSurfaceInput): boolean {
  if (input.allowQueryOverride != null) return input.allowQueryOverride;
  return import.meta.env.DEV === true;
}

function searchRequestsAskSurface(search: string): boolean {
  const raw = search.startsWith("?") ? search.slice(1) : search;
  return new URLSearchParams(raw).get("surface") === "ask";
}

export function readInPlaceAskSurface(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(ASK_V2_INPLACE_STORAGE) === "1";
  } catch {
    return false;
  }
}

/** Returns whether storage now matches `on`. Failed writes must not reload. */
export function persistInPlaceAskSurface(on: boolean): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (on) {
      window.localStorage.setItem(ASK_V2_INPLACE_STORAGE, "1");
    } else {
      window.localStorage.removeItem(ASK_V2_INPLACE_STORAGE);
    }
    return readInPlaceAskSurface() === on;
  } catch {
    return false;
  }
}

/**
 * True when host / query / env alone would mount V2, ignoring the in-place
 * marker. Used so a PostHog-off user on ask.puntt.ai is not bounced to V1.
 */
export function isAskSurfaceLockedByHostOrOverride(
  input: AskSurfaceInput = {},
): boolean {
  return isAskSurface({ ...input, inPlace: false });
}

/** Page-load answer for the no-override call. `undefined` until first asked. */
let pageLoadSurface: boolean | undefined;

/**
 * Drops the page-load snapshot so each test owns its own state. Any spec that
 * calls `isAskSurface()` with no override must call this in `beforeEach` — the
 * runner shuffles, and the snapshot would otherwise carry one test's answer
 * into the next.
 *
 * Callers live in `__tests__/askSurface.test.ts` ("re-reads storage after the
 * test-only reset" fails if this stops clearing the snapshot). `no-unused-vars`
 * does not flag unused exports, so that spec is the only thing keeping this
 * from going dead — delete it there before deleting it here.
 */
export function resetAskSurfaceForTests(): void {
  pageLoadSurface = undefined;
}

function hasSurfaceOverride(input: AskSurfaceInput): boolean {
  return (
    input.hostname !== undefined ||
    input.search !== undefined ||
    input.envFlag !== undefined ||
    input.inPlace !== undefined ||
    input.allowQueryOverride !== undefined
  );
}

/**
 * True when this page load should mount the V2 Ask tree instead of the
 * tickets app. Pure besides the window/env defaults — pass overrides in tests.
 *
 * The no-override answer is snapshotted for the page load. why: the route
 * tree is picked once, at router construction (`selectChildRoutes`), and
 * every later caller — post-auth landing, LoginView's `/roi` divergence,
 * `applyAskV2InPlaceReload`'s `mountedAskSurface` — is really asking "is the
 * V2 tree mounted". Re-reading storage answered "no" the moment logout
 * cleared the marker (`persistInPlaceAskSurface(false)` in the auth slice
 * does not reload), which sent the still-mounted V2 tree to `/tickets` —
 * a route `askV2ChildRoutes` does not register.
 */
export function isAskSurface(input: AskSurfaceInput = {}): boolean {
  if (hasSurfaceOverride(input)) return resolveAskSurface(input);
  if (pageLoadSurface === undefined) pageLoadSurface = resolveAskSurface(input);
  return pageLoadSurface;
}

function resolveAskSurface(input: AskSurfaceInput): boolean {
  const envFlag = input.envFlag ?? ASK_SURFACE_ENV;
  if (envFlagOn(envFlag)) return true;

  const search = input.search ?? readWindowSearch();
  if (queryOverrideAllowed(input) && searchRequestsAskSurface(search)) {
    return true;
  }

  const hostname = input.hostname ?? readWindowHostname();
  if (isAskHostname(hostname)) return true;

  const inPlace = input.inPlace ?? readInPlaceAskSurface();
  return inPlace;
}
